import axios, { AxiosRequestConfig, AxiosError } from 'axios';
import {
  AccountLockoutErrorPayload,
  AccountLockoutErrorResponseBody,
  ErrorResponseEnum,
} from '@goalsetter/classroom-types/auth';

type LoginApiErrorBody = Partial<AccountLockoutErrorResponseBody> & {
  code?: number | string;
};

export const isAccountLockoutCode = (code: unknown): boolean =>
  Number(code) === ErrorResponseEnum.AccountTemporarilyLocked;

export const isTenantInactiveCode = (code: unknown): boolean =>
  Number(code) === ErrorResponseEnum.TenantInactive;

export const isTenantInactiveError = (error: unknown): boolean =>
  isTenantInactiveCode(parseLoginApiError(error)?.code);

export const isAccountLockoutError = (
  error: LoginApiErrorBody | null,
): error is AccountLockoutErrorResponseBody =>
  Boolean(error?.lockout && isAccountLockoutCode(error.code));

export const resolveRequestUrl = (
  config: AxiosRequestConfig | undefined,
): string => {
  if (!config?.url) {
    return '';
  }
  if (/^https?:\/\//i.test(config.url)) {
    return config.url;
  }
  const baseUrl = config.baseURL ?? '';
  return `${baseUrl}${config.url}`;
};

export const isPublicLoginRequestUrl = (url: string): boolean =>
  url.includes('/api/auth/public/login');

export const isPublicLoginRequest = (
  config: AxiosRequestConfig | undefined,
): boolean => isPublicLoginRequestUrl(resolveRequestUrl(config));

export const getAxiosErrorResponseData = (
  error: AxiosError<LoginApiErrorBody>,
): LoginApiErrorBody | undefined => {
  const data = error.response?.data;
  if (typeof data === 'string') {
    try {
      return JSON.parse(data) as LoginApiErrorBody;
    } catch {
      return undefined;
    }
  }
  if (typeof data === 'object' && data !== null) {
    return data;
  }
  return undefined;
};

export const isAccountTemporarilyLockedAxiosError = (
  error: unknown,
): error is AxiosError<LoginApiErrorBody> => {
  if (!axios.isAxiosError<LoginApiErrorBody>(error)) {
    return false;
  }
  const data = getAxiosErrorResponseData(error);
  return isAccountLockoutCode(data?.code);
};

export const shouldSkipAuthRefresh = (
  config: AxiosRequestConfig | undefined,
): boolean => Boolean(config?.skipAuthRefresh);

export const parseLoginApiError = (
  error: unknown,
): LoginApiErrorBody | null => {
  if (axios.isAxiosError<LoginApiErrorBody>(error)) {
    return getAxiosErrorResponseData(error) ?? null;
  }
  if (typeof error === 'object' && error !== null && 'code' in error) {
    return error as LoginApiErrorBody;
  }
  return null;
};

export const getAccountLockoutFromError = (
  error: unknown,
): AccountLockoutErrorPayload | null => {
  const loginError = parseLoginApiError(error);
  if (!isAccountLockoutError(loginError)) {
    return null;
  }
  return loginError.lockout;
};

export const getLockoutRemainingSeconds = (
  lockout: AccountLockoutErrorPayload,
): number => {
  if (!lockout.lockedUntil) {
    return 0;
  }
  const unlockAtMs = Date.parse(lockout.lockedUntil);
  if (Number.isNaN(unlockAtMs)) {
    return 0;
  }
  return Math.max(0, Math.ceil((unlockAtMs - Date.now()) / 1000));
};
